/**
 * Row-level actions menu for tables (standard UX).
 *
 * - Trigger: kebab button (`MoreHorizontal`), outline `variant`, fixed size for the column cell.
 * - Menu rendered via `createPortal` + `position: fixed` so table `overflow: hidden/auto` does not clip it.
 * - Each item: `icon` (lucide or compatible component) + `label`; `destructive` for delete/dangerous actions.
 * - Peer: `lucide-react` (trigger icon + per-row icons).
 *
 * @example
 * ```tsx
 * <TableCell className="text-right align-middle">
 *   <TableRowActionsMenu
 *     triggerWrapperClassName="flex w-full justify-end"
 *     items={[
 *       { id: 'edit', label: 'Edit', icon: Pencil, onClick: () => {} },
 *       { id: 'del', label: 'Delete', icon: Trash2, destructive: true, onClick: () => {} },
 *     ]}
 *   />
 * </TableCell>
 * ```
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';
import Button from '../Button';
import { cn } from '../cn';
function computePlacement(trigger, menuHeightEstimate) {
    const gap = 8;
    const margin = 8;
    const spaceBelow = window.innerHeight - trigger.bottom - gap;
    const openUp = spaceBelow < menuHeightEstimate && trigger.top - gap > window.innerHeight - trigger.bottom;
    const top = openUp
        ? Math.max(margin, trigger.top - gap - menuHeightEstimate)
        : Math.min(Math.max(trigger.bottom + gap, margin), window.innerHeight - margin);
    const left = Math.min(Math.max(trigger.right, margin), window.innerWidth - margin);
    return { top, left, origin: openUp ? 'top' : 'bottom' };
}
export const TableRowActionsMenu = ({ items, triggerLabel = 'Open actions', className, triggerWrapperClassName, }) => {
    const menuId = useId();
    const triggerRef = useRef(null);
    const menuPanelRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [placement, setPlacement] = useState(null);
    const close = useCallback(() => setOpen(false), []);
    const updatePlacement = useCallback(() => {
        const el = triggerRef.current;
        if (!el)
            return;
        const rect = el.getBoundingClientRect();
        const estimate = Math.min(320, 44 * Math.max(1, items.length));
        setPlacement(computePlacement(rect, estimate));
    }, [items.length]);
    useLayoutEffect(() => {
        if (!open) {
            setPlacement(null);
            return;
        }
        updatePlacement();
    }, [open, updatePlacement]);
    useEffect(() => {
        if (!open)
            return;
        const onScrollOrResize = () => updatePlacement();
        window.addEventListener('resize', onScrollOrResize);
        window.addEventListener('scroll', onScrollOrResize, true);
        return () => {
            window.removeEventListener('resize', onScrollOrResize);
            window.removeEventListener('scroll', onScrollOrResize, true);
        };
    }, [open, updatePlacement]);
    useEffect(() => {
        if (!open)
            return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape')
                close();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, close]);
    useEffect(() => {
        if (!open)
            return;
        const onMouseDown = (event) => {
            var _a, _b;
            const t = event.target;
            if (!t)
                return;
            if ((_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.contains(t))
                return;
            if ((_b = menuPanelRef.current) === null || _b === void 0 ? void 0 : _b.contains(t))
                return;
            close();
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [open, close]);
    if (items.length === 0)
        return null;
    const menuPanel = open && placement ? (React.createElement("div", { ref: menuPanelRef, id: menuId, role: "menu", "data-kg-table-row-actions": "", className: cn('fixed z-[9999] min-w-[11rem] max-h-[min(320px,calc(100vh-16px))] overflow-y-auto rounded-md border border-border bg-background p-1 text-foreground shadow-lg', placement.origin === 'bottom' ? 'origin-top' : 'origin-bottom'), style: {
            top: placement.top,
            left: placement.left,
            transform: 'translateX(-100%)',
        }, onClick: (e) => e.stopPropagation() }, items.map((item) => {
        const Icon = item.icon;
        return (React.createElement("button", { key: item.id, type: "button", role: "menuitem", disabled: item.disabled, className: cn('flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2', item.destructive
                ? 'text-destructive hover:bg-red-50 disabled:opacity-50'
                : 'hover:bg-muted disabled:opacity-50'), onClick: () => {
                if (item.disabled)
                    return;
                close();
                item.onClick();
            } },
            React.createElement(Icon, { className: "h-3.5 w-3.5 shrink-0", "aria-hidden": true }),
            item.label));
    }))) : null;
    return (React.createElement("div", { className: cn('relative inline-flex', triggerWrapperClassName, className), "data-kg-table-row-actions": "" },
        React.createElement(Button, { ref: triggerRef, type: "button", variant: "outline", size: "sm", title: triggerLabel, "aria-label": triggerLabel, "aria-haspopup": "menu", "aria-expanded": open, "aria-controls": open ? menuId : undefined, className: "h-8 w-8 shrink-0 p-0", onClick: (e) => {
                e.stopPropagation();
                setOpen((v) => !v);
            } },
            React.createElement(MoreHorizontal, { className: "h-4 w-4", "aria-hidden": true })),
        typeof document !== 'undefined' && menuPanel ? createPortal(menuPanel, document.body) : null));
};
export default TableRowActionsMenu;
